import { useCallback } from "react"
import { Accordion, Button, Heading, Text, VStack } from "@chakra-ui/react"
import {
  captureMessage,
  ErrorBoundary,
  ErrorBoundaryProps,
  setExtra,
} from "@sentry/react"

import AccordionItem from "#/shared/components/AccordionItem"
import GridContainer from "#/shared/components/GridContainer"
import { useEffectOnce } from "#/shared/hooks"

type ErrorComponentProps = {
  error: Error
  resetError: () => void
}

const formatErrorStack = (stack: string | undefined) =>
  stack
    ? stack
        .split("\n")
        .map((el) => el.trim())
        .slice(1)
    : []

function ErrorComponent({ error, resetError }: ErrorComponentProps) {
  const errorHandler = useCallback(
    (message: string) => {
      setExtra("error", error)
      captureMessage(message, {
        level: "info",
      })
    },
    [error],
  )

  const renderedErrorHandler = useCallback(() => {
    errorHandler(
      `Page with refresh button was displayed for error - ${error.message}`,
    )
  }, [error.message, errorHandler])

  const refreshButtonHandler = useCallback(() => {
    errorHandler(`Page was refreshed after error - ${error.message}`)
    resetError()

    // Reload the page after error was sent
    window.location.reload()
  }, [error.message, errorHandler, resetError])

  useEffectOnce(() => {
    renderedErrorHandler()
  })

  return (
    <GridContainer>
      <VStack textAlign="center">
        <Heading variant="h3" mb={4}>
          You have encountered an error
        </Heading>
        <Accordion allowToggle w="100%">
          <AccordionItem
            title={
              <Text fontSize="20px" variant="body">
                See full error
              </Text>
            }
            accordionItemProps={{ w: "100%" }}
            accordionButtonProps={{ w: "100%", justifyContent: "center" }}
            accordionPanelProps={{ bg: "primary.black.5", py: 4 }}
          >
            <Heading variant="h4" mb={3}>
              {error.name}: {error.message}
            </Heading>
            {formatErrorStack(error.stack).map((el, index) => (
              // eslint-disable-next-line react/no-array-index-key
              <Text mb={1} key={index}>
                {el}
              </Text>
            ))}
          </AccordionItem>
        </Accordion>
        <Text my={6}>Click button to refresh the page.</Text>
        <Button variant="primary" size="large" onClick={refreshButtonHandler}>
          Click here to refresh
        </Button>
      </VStack>
    </GridContainer>
  )
}

export default function ErrorWrapper({
  ...props
}: Omit<ErrorBoundaryProps, "fallback">) {
  return (
    <ErrorBoundary
      fallback={({ error, resetError }) => (
        <ErrorComponent error={error} resetError={resetError} />
      )}
      {...props}
    >
      {props.children}
    </ErrorBoundary>
  )
}
